
<template>
  <v-app
    id="app"
    :style="cssVars"
  >
  <div
    id="main-content"
  >

    <WorldWideTelescope
      :wwt-namespace="wwtNamespace"
      :class="{ pointer: lastClosePt !== null }"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointerdown="onPointerDown"
    ></WorldWideTelescope>
    
    <v-overlay
      :model-value="showSplashScreen"
      absolute
      opacity="0.6"
      :style="cssVars"
      id="splash-overlay"
    >
      <div
        id="splash-screen"
        v-click-outside="closeSplashScreen"
        :style="cssVars"
      >
        <div
          id="first-splash-row"
        >
          <font-awesome-icon
            id="close-splash-button"
            @click="closeSplashScreen"
            icon="xmark"
          />
          <div id="splash-screen-text">
            <p>Want to see how</p>
            <p>the <span class="highlight">"Green Comet"</span></p>
            <p>moves in our sky?</p>
          </div>
        </div>

        <div>
          <v-btn
            class="splash-get-started"
            @click="closeSplashScreen"
            :color="accentColor"
            :density="xSmallSize ? 'compact' : 'default'"
            size="x-large"
            variant="elevated"
            rounded="lg"
          >
            Get Started
          </v-btn>
        </div>
        
        <div id="splash-screen-acknowledgements">
          Brought to you by <a href="https://www.cosmicds.cfa.harvard.edu/" target="_blank" rel="noopener noreferrer">Cosmic Data Stories</a> and <a href="https://www.worldwidetelescope.org/home/" target="_blank" rel="noopener noreferrer">WorldWide Telescope</a>.
          
          <div id="splash-screen-logos">
            <credit-logos
              :default-logos="['cosmicds', 'wwt', 'sciact', 'nasa-grantee']"
            />
          </div>
        </div>
      </div>
    </v-overlay>

    <transition name="fade">
      <div
        class="modal"
        id="modal-loading"
        v-show="isLoading"
      >
        <div class="container">
          <div class="spinner"></div>
          <p>Loading …</p>
        </div>
      </div>
    </transition>

    <div class="top-content">
      <div
        id="video-icon-dummy"
        class="control-icon-wrapper"
      >
        <font-awesome-icon
          id="video-icon"
          class="control-icon"
          icon="video"
          size="lg"
        ></font-awesome-icon>
      </div>
      <div id="center-buttons">
        <icon-button
          v-model="showLocationSelector"
          fa-icon="location-pin"
          id="map-icon"
          :color="cometColor"
          tooltip-text="Select location"
          tooltip-location="bottom"
          tooltip-offset="5px"
        ></icon-button> 
      </div>
      <div id="right-buttons">
        <icon-button
          v-model="showTextSheet"
          id="text-icon"
          :color="cometColor"
          fa-icon="book-open"
          tooltip-text="Learn more"
          tooltip-location="start"
          :tooltip-offset="smallSize ? 0 : '10px'"
        ></icon-button>
        <icon-button
          v-model="showVideoSheet"
          id="video-icon"
          fa-icon="video"
          :color="cometColor"
          tooltip-text="Watch video"
          tooltip-location="start"
          :tooltip-offset="smallSize ? 0 : '10px'"
        ></icon-button>
      </div>
    </div>
    
    <div class="left-content">
      <folder-view
        v-if="imagesetFolder !== null"
        class="folder-view"
        sliders
        expandable
        :thumbnails="true"
        :open="mobile ? false : true"
        :root-folder="imagesetFolder"
        :wwt-namespace="wwtNamespace"
        :incomingItemSelect="incomingItemSelect"
        flex-direction="column"
        @select="onItemSelected"
        @opacity="onOpacityChanged"
        @toggle="onToggle"
      ></folder-view>
    </div>
    
    <div class="bottom-content">
      <div
        id="controls"
        class="control-icon-wrapper"
      >
        <div id="controls-top-row">
          <font-awesome-icon
            size="lg"
            class="ma-1"
            :color="cometColor"
            :icon="showControls ? `chevron-down` : `gear`"
            @click="showControls = !showControls"
            @keyup.enter="showControls = !showControls"
            tabindex="0"
          /> 
        </div>
        <transition-expand>
          <div v-if="showControls" class="controls-content">
            <v-checkbox
              :color="cometColor"
              v-model="showAltAzGrid"
              @keyup.enter="showAltAzGrid = !showAltAzGrid"
              label="Grid"
              hide-details
            />
            <v-checkbox
              :color="cometColor"
              v-model="showConstellations"
              @keyup.enter="showConstellations = !showConstellations"
              label="Constellations"
              hide-details
            />
            <v-checkbox
              :color="cometColor"
              v-model="showHorizon"
              @keyup.enter="showHorizon = !showHorizon"
              label="Horizon"
              hide-details
            />
            <div
              style="color:white;"
              class="mt-3"
            >
              Selected location's time:
            </div>
            <date-picker
              dark
              time-picker
              enable-seconds
              :is-24="false"
              v-model="timeOfDay"
              :clearable="false"
              close-on-scroll
              class="mb-4 mt-1"
            >
              <template #input-icon>
                <font-awesome-icon
                  icon="clock"
                  class="mx-2"
                  :color="cometColor"
                ></font-awesome-icon>
              </template>
            </date-picker>
            <v-btn
              block
              :color="cometColor"
              @click="centerOnCurrentDate"
              @keyup.enter="centerOnCurrentDate"
              class="mb-2"
            >
              Center on Now
            </v-btn>
            <v-btn
              block
              :color="cometColor"
              @click="() => {
                playing = false;
                playingCometPath = !playingCometPath;
              }"
            >
              {{ `${playingCometPath ? 'Stop' : 'Play'} comet images` }}
            </v-btn>
            <!--
            <v-btn
              block
              :color="cometColor"
              @click="setToFirstCometImage"
            >
              Best view for comet images
            </v-btn> -->
          </div>
        </transition-expand>
      </div>
      <div id="tools">
        <span class="tool-container">
          <!-- <v-chip
            id="sliderlabel"
            outlined
            label
            >
            Date
          </v-chip> -->
          <icon-button
            id="play-pause-icon"
            :fa-icon="!(playing || playingCometPath) ? 'play' : 'pause'"
            @activate="() => {
              playing = !(playing || playingCometPath);
              playingCometPath = false;
            }"
            :color="ephemerisColor"
            tooltip-text="Play/Pause"
            tooltip-location="top"
            tooltip-offset="5px"
          ></icon-button>
          <vue-slider
            id="slider"
            adsorb
            included
            :marks="(d: number) => {
              return allDates.includes(d) || cometImageDates.includes(d);
            }"
            :order="false"
            v-model="selectedTime"
            @change="onTimeSliderChange"
            :data="dates"
            tooltip="always"
            :tooltip-formatter="(v: number) => 
              toDateString(new Date(v))
            "
            >
              <template v-slot:mark="{ pos, value }">
                <div
                  :class="[{ 'mark-line': cometImageDates.includes(value) }]"
                  :style="{ left: `${pos}%` }">
                </div>
              </template>
            </vue-slider>
          </span>
      </div>
      <div id="credits" class="ui-text">
        <div id="icons-container">
          <credit-logos
            :default-logos="['cosmicds', 'wwt', 'sciact', 'nasa-grantee']"
          />
          <!-- <ShareNetwork
            v-for="network in networks"
            :key="network.name"
            :network="network.name"
            :class="`${network.name}-button`"
            :style="{ backgroundColor: network.color, width: 'fit-content' }"
            :description="description"
            :url="url"
            :title="title"
            :hashtags="hashtagString"
            :quote="description"
            twitter-user="WWTelescope"
          >
            <font-awesome-icon
              :class="`${network.name}-icon`"
              :icon="['fab', network.name]"
              size="lg"
            ></font-awesome-icon>
          </ShareNetwork> -->
        </div>
      </div>
    </div>

    <!-- WebGL2 not enabled dialog --> 
    <v-dialog
      class="error-dialog"
      :style="cssVars"
      v-model="showWebGL2Warning"
      persistent
    >
      <v-card>
        <div class="error-message">
          <p>
            <strong>This app requires WebGL 2</strong> 
          </p>
          <p class="mt-2">
            Check your browser's settings and enable WebGL 2 ("graphics acceleration" on some browsers).
          </p> 
          <p class="mt-2">
            You can check whether your browser supports WebGL 2
            and get assistance <a href="https://get.webgl.org/webgl2/" target="_blank" rel="noopener noreferrer">here</a>.
          </p> 
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      id="video-container"
      v-model="showVideoSheet"
      transition="slide-y-transition"
      fullscreen
    >
      <div class="video-wrapper">
        <font-awesome-icon
          id="video-close-icon"
          class="close-icon"
          icon="times"
          size="lg"
          @click="showVideoSheet = false"
          @keyup.enter="showVideoSheet = false"
          tabindex="0"
        ></font-awesome-icon>
        <video
          controls
          id="info-video"
        >
          <source src="./assets/video2.mp4" type="video/mp4">
        </video>
      </div>
    </v-dialog>

    <v-dialog
      id="location-dialog"
      v-model="showLocationSelector"
    >
      <v-card id="location-selector">
        <div
          class="text-center"
        >
          Move around the map and double-click to change location
        </div>
        <v-btn
          @click="getLocation"
          @keyup.enter="getLocation"
        >
          Use My Location
        </v-btn>
        <div class="text-center red--text">{{  locationErrorMessage  }}</div>
        <div id="map-container"></div>
      </v-card>
    </v-dialog>

    <v-dialog
      class="bottom-sheet"
      id="text-bottom-sheet"  
      hide-overlay
      persistent
      no-click-animation
      absolute
      width="100%"
      :scrim="false"
      location="bottom"
      v-model="showTextSheet"
      transition="dialog-bottom-transition"
    >
      <v-card height="100%">
      <v-tabs
        v-model="tab"
        height="32px"
        :color="cometColor"
        :slider-color="cometColor"
        id="tabs"
        dense
        grow
      >
        <v-tab tabindex="0"><h3>Information</h3></v-tab>
        <v-tab tabindex="0"><h3>Using WWT</h3></v-tab>
      </v-tabs>
      <font-awesome-icon
        id="close-text-icon"
        class="control-icon"
        icon="times"
        size="lg"
        @click="showTextSheet = false"
        @keyup.enter="showTextSheet = false"
        tabindex="0"
      ></font-awesome-icon>
        <v-window v-model="tab" id="tab-items" class="pb-2 no-bottom-border-radius">
          <v-window-item>
            <v-card class="no-bottom-border-radius scrollable">
              <v-card-text class="info-text no-bottom-border-radius">
                
                Comets are dusty snowballs, large clumps of rock and ice, that originate in the outer solar system. The “Green Comet” (or <a href="https://science.nasa.gov/comet-2022-e3-ztf" target="_blank" rel="noopener noreferrer">C/2022 E3</a>) makes its closest approach to the Sun (and to Earth) in early 2023. The comet images in this interactive view were taken on different dates from December 2022 through January 2023 by astrophotographer <a href="http://www.astrostudio.at/" target="_blank" rel="noopener noreferrer">Gerald Rhemann</a>.
                <br><br>

                <h3>Explore!</h3>
                Adjusting the <span class="ui-element-ref-comet">Date</span> slider along the bottom of the screen shows you the position of the comet since it was discovered in March 2022. 
                <ul class="text-list">
                  <li>Move the date slider forward and backward. Observe how the comet moves in the sky with time. Can you find when the comet is moving fastest in the sky and when it is moving slowest in the sky? Can you find when the comet path “twirls” in the sky? (This is known as “retrograde motion.”)</li>
                  <li>Click the thumbnail images on the left to see photographs of the comet in the virtual sky. Use the sliders under the thumbnails to display the comet images for multiple dates.</li>
                  <li>Click "Play comet images" to view them in order by date. What do you notice about the direction of the comet’s tails relative to the motion of the comet?</li>
                </ul>
                <br>

                <h3>Why is the Comet Green?</h3>
                Visually, comets have three parts: the bright coma and two tails. The coma is material being vaporized off the comet’s surface by radiation from the Sun. The green color of the comet is due to dicarbon (a molecule made of two carbon atoms, C<sub>2</sub>) in the comet’s coma. When excited by ultraviolet light from the sun, dicarbon fluoresces with a blue-green light. 
                <br><br>

                <h3>A Comet’s Tail</h3>
                The comet has an ionized gas tail and a dust tail. The gas tail is composed of the gas being blown off the comet’s surface and ionized (given an electric charge) by ultraviolet radiation from the Sun. This charged gas is blown in a direction <strong>away</strong> from the Sun by the solar wind. The dust tail is made of dust blown off the surface by jets of vaporizing gas. The dust is electrically neutral and does not get pushed as hard by the solar wind, which is why it is generally distinct from the gas tail. Many people think that the tails trail behind the comet as it moves forward. You can see from the images that the solar wind is blowing the tails ahead of the comet for much of its path through space!

                <br><br><br>
                <div class="credits">
                <h3>Credits:</h3>
                <h4><a href="https://www.cosmicds.cfa.harvard.edu/" target="_blank" rel="noopener noreferrer">CosmicDS</a> Team:</h4>
                Jon Carifio<br>
                John Lewis<br>
                Pat Udomprasert<br>
                Alyssa Goodman<br>
                Mary Dussault<br>
                Harry Houghton<br>
                Anna Nolin<br>
                Evaluator: Sue Sunbury<br>
                <br>
                <h4>WorldWide Telescope Team:</h4>
                Peter Williams<br>
                A. David Weigel<br>
                Jon Carifio<br>
                <br>
                <funding-acknowledgment/>
                </div>
                <v-spacer class="end-spacer"></v-spacer>
              </v-card-text>
            </v-card>
          </v-window-item>
          <v-window-item>
            <v-card class="no-bottom-border-radius scrollable" style="height: 100%;">
              <v-card-text class="info-text no-bottom-border-radius">
                <v-container>
                  <v-row align="center">
                  <v-col cols="4">
                      <v-chip
                        label
                        outlined
                      >
                        Pan
                      </v-chip>
                    </v-col>
                    <v-col cols="8" class="pt-1">
                      <strong>{{ touchscreen ? "press + drag" : "click + drag" }}</strong>  {{ touchscreen ? ":" : "or" }}  <strong>{{ touchscreen ? ":" : "W-A-S-D" }}</strong> {{ touchscreen ? ":" : "keys" }}<br>
                    </v-col>
                  </v-row>
                  <v-row align="center">
                    <v-col cols="4">
                      <v-chip
                        label
                        outlined
                      >
                        Zoom
                      </v-chip>
                    </v-col>
                    <v-col cols="8" class="pt-1">
                      <strong>{{ touchscreen ? "pinch in and out" : "scroll in and out" }}</strong> {{ touchscreen ? ":" : "or" }} <strong>{{ touchscreen ? ":" : "I-O" }}</strong> {{ touchscreen ? ":" : "keys" }}<br>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <div
                        style="min-height: 120px;"
                      >
                        <h4>Tips:</h4>
                        <ul class="text-list">
                          <li>
                            Click <font-awesome-icon
                                  class="control-icon"
                                  icon="location-pin"
                                  size="lg" 
                                ></font-awesome-icon>
                            to adjust your location.
                          </li>
                          <li>
                            Adjust the date slider at the bottom to see the location of the Green Comet on a particular day.
                          </li>
                          <li>
                            Click 
                              <font-awesome-icon
                                id="play-pause-icon"
                                class="control-icon"
                                icon="play"
                                size="lg"
                              ></font-awesome-icon>
                            to auto-advance time.
                          </li>
                          <li>
                            The small pink markers in the sky show the path of the comet on a particular date at 00:00 UT. The larger pink marker shows the position of the comet at the time displayed on the clock. Larger white'ish dots overlaid on the small pink dots indicate dates that have comet photographs.
                          </li>
                          <li>
                            Click a thumbnail image in the panel in the upper left to see an image of the comet photographed by Gerald Rhemann overlaid on the virtual sky on that day. The slider under the thumbnail adjusts the image opacity within the sky view.
                          </li>
                          <li>
                            Depending on what you are trying to see, you may want to try different zoom levels. Zooming out will help you see the comet's overall path better. Zooming in will help you see more details in the comet images.
                          </li>                          
                          <li>
                            Choose whether to display the sky grid, constellations, or the horizon.
                          </li>
                          <li>
                            Adjust the displayed time for your chosen location using the time controller.
                          </li>
                          <li>
                            Re-center the view on the comet's location "now" or Play the comet images, in order, along their path.
                          </li>
                        </ul>
                      <br>
                      This Data Story is powered by WorldWide Telescope (WWT).

                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <div class="credits">
                      <h3>Credits:</h3>
                      <h4><a href="https://www.cosmicds.cfa.harvard.edu/" target="_blank" rel="noopener noreferrer">CosmicDS</a> Team:</h4>
                      Jon Carifio<br>
                      John Lewis<br>
                      Pat Udomprasert<br>
                      Alyssa Goodman<br>
                      Mary Dussault<br>
                      Harry Houghton<br>
                      Anna Nolin<br>
                      Evaluator: Sue Sunbury<br>
                      <br>
                      <h4>WorldWide Telescope Team:</h4>
                      Peter Williams<br>
                      A. David Weigel<br>
                      Jon Carifio<br>
                      </div>
                      <v-spacer class="end-spacer"></v-spacer>
                    </v-col>
                  </v-row>
                </v-container>              
              </v-card-text>
            </v-card>
          </v-window-item>
        </v-window>
      </v-card>
    </v-dialog>

    <v-container>
      <v-expand-transition>
        <user-experience
          v-if="showRating"
          :question="question"
          icon-size="3x"
          @dismiss="(_rating: UserExperienceRating | null, _comments: string | null) => {
            showRating = false;
          }"
          @rating="(rating: UserExperienceRating | null) => {
            currentRating = rating;
            updateUserExperienceInfo(currentRating, currentComments);
          }"
          @finish="(rating: UserExperienceRating | null, comments: string | null) => {
            currentRating = rating;
            currentComments = comments;
            updateUserExperienceInfo(currentRating, currentComments);
            showRating = false;
          }"
        >
          <template #footer>
            <div id="user-experience-footer" class="mt-4">
              <v-btn
                class="rating-opt-put"
                color="#BDBDBD"
                size="small"
                variant="text"
                @click="onOptOutClicked"
              >
              Don't show again
              </v-btn>
              <v-btn
                class="privacy-button"
                color="#BDBDBD"
                size="small"
                @click="showPrivacyPolicy = true"
                @keyup.enter="showPrivacyPolicy = true"
                target="_blank"
                rel="noopener noreferrer"
              >
              What is this?
              </v-btn>
            </div>
          </template>
        </user-experience>
      </v-expand-transition>
      <cds-privacy-policy v-model="showPrivacyPolicy" />
    </v-container>

    <notifications group="startup-location" position="top right" />

  </div>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { csvFormatRows, csvParse } from "d3-dsv";

import { distance } from "@wwtelescope/astro";
import { Color, Constellations, Folder, Grids, Layer, LayerManager, Poly, RenderContext, Settings, SpreadSheetLayer, WWTControl } from "@wwtelescope/engine";
import { MarkerScales, PlotTypes, Thumbnail } from "@wwtelescope/engine-types";

import L, { LeafletMouseEvent, Map } from "leaflet";
import { getTimezoneOffset } from "date-fns-tz";
import tzlookup from "tz-lookup";
import { v4 } from "uuid";
import { API_BASE_URL, MiniDSBase, BackgroundImageset, skyBackgroundImagesets, type UserExperienceRating } from "@cosmicds/vue-toolkit";

import { ImageSetLayer, Place } from "@wwtelescope/engine";
import { applyImageSetLayerSetting } from "@wwtelescope/engine-helpers";

import { drawSkyOverlays, initializeConstellationNames, makeAltAzGridText, drawSpreadSheetLayer, layerManagerDraw } from "./wwt-hacks";

interface MoveOptions {
  instant?: boolean;
  zoomDeg?: number;
  rollRad?: number;
}

import {
  ephemerisFullDatesCsv,
  ephemerisCometImageDatesCsv
} from "./data";

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

const SECONDS_PER_DAY = 60 * 60 * 24;
const MILLISECONDS_PER_DAY = 1000 * SECONDS_PER_DAY;

function parseCsvTable(csv: string) {
  return csvParse(csv, (d) => {
    return {
      date: new Date(d.Date ?? ""),
      ra: +(d.RA ?? ""),
      dec: +(d.Dec ?? ""),
      tMag: +(d.Tmag ?? ""),
      angspeed: +(d.SkyMotion ?? ""),
    };
  });
}
const fullDatesTable = parseCsvTable(ephemerisFullDatesCsv);
const cometImageDatesTable = parseCsvTable(ephemerisCometImageDatesCsv);

// NB: The two tables have identical structures.
// We aren't exporting these types anywhere, so
// generic names are fine
type Table = typeof fullDatesTable;
type TableRow = typeof fullDatesTable[number];

function formatCsvTable(table: Table): string {
  return csvFormatRows([[
    "Date", "RA", "Dec", "Tmag" , "Angspeed"
  ]].concat(table.map((d, _i) => {
    return [
      d.date.toISOString(),
      d.ra.toString(),
      d.dec.toString(),
      d.tMag.toString(),
      d.angspeed.toString(),
    ];
  }))).replace(/\n/g, '\r\n');
  // By using a regex, we replace all instances.
  // For WWT implementation reasons (left over from 
  // the Windows client?), we need the line endings 
  // to be CRLF
}

const fullDatesString = formatCsvTable(fullDatesTable);
const cometImageDatesString = formatCsvTable(cometImageDatesTable);

const allDates = fullDatesTable.map(r => r.date.getTime());
const cometImageDates = cometImageDatesTable.map(r => r.date.getTime());
const minDate = Math.min(...allDates, ...cometImageDates);
const maxDate = Math.max(...allDates, ...cometImageDates);
const dates: number[] = [];

const d = new Date(minDate);
let t = d.getTime();
while (t <= maxDate) {
  dates.push(t);
  dates.push(t + 86400000/2);
  d.setUTCDate(d.getUTCDate() + 1);
  t = d.getTime();
}

type LocationRad = {
  longitudeRad: number;
  latitudeRad: number;
};

type EquatorialRad = {
  raRad: number;
  decRad: number;
};

type HorizontalRad = {
  altRad: number;
  azRad: number;
};

type SheetType = "text" | "video" | null;

export default defineComponent({
  extends: MiniDSBase,
  
  props: {
    wtml: {
      type: Object,
      required: true
    },
    wwtNamespace: {
      type: String,
      required: true
    },
    url: {
      type: String,
      required: true
    },
    thumbnailUrl: {
      type: String,
      required: true
    },
    bgWtml: {
      type: String,
      required: true
    },
    bgName: {
      type: String,
      required: true
    }
  },
  data() {
    const now = new Date();
    const maybeUUID = window.localStorage.getItem("cds-green-comet-uuid");
    const uuid = maybeUUID ?? v4();
    const ratingOptedOut = window.localStorage.getItem("cds-green-comet-rating-optout")?.toLowerCase() === "true";
    return {
      showWebGL2Warning: false,

      showSplashScreen: true,
      imagesetLayers: {} as Record<string, ImageSetLayer>,
      layersLoaded: false,
      positionSet: false,
      imagesetFolder: null as Folder | null,
      backgroundImagesets: [] as BackgroundImageset[],

      playing: false,
      playingCometPath: false,
      playingIntervalId: null as ReturnType<typeof setInterval> | null,
      playingWaitCount: 0,

      showAltAzGrid: true,
      showConstellations: false,
      showHorizon: false,

      currentCometImageLayer: null as SpreadSheetLayer | null,
      currentAllLayer: null as SpreadSheetLayer | null,
      interpolatedDailyTable: null as Table | null,

      cometImageDates: cometImageDates,
      allDates: allDates,
      dates: dates,
      
      lastClosePt: null as TableRow | null,
      ephemerisColor: "#D60493",
      cometColor: "#04D6B0",
      todayColor: "#D6B004",

      incomingItemSelect: null as Thumbnail | null,
      
      sheet: null as SheetType,
      showMapTooltip: false,
      showTextTooltip: false,
      showVideoTooltip: false,
      showPlayPauseTooltip: false,
      showLocationSelector: false,
      showControls: false,
      tab: 0,

      circle: null as L.Circle | null,
      map: null as Map | null,

      selectionProximity: 4,
      pointerMoveThreshold: 6,
      isPointerMoving: false,
      pointerStartPosition: null as { x: number; y: number } | null,

      // Harvard Observatory
      timeOfDay: { hours: now.getHours(), minutes: now.getMinutes(), seconds: now.getSeconds() },
      selectedTime: now.setUTCHours(0, 0, 0, 0),
      selectedTimezone: "America/New_York",
      location: {
        latitudeRad: D2R * 42.3814,
        longitudeRad: D2R * -71.1281
      } as LocationRad,
      locationErrorMessage: "",

      showRating: false,
      storyRatingUrl: `${API_BASE_URL}/green-comet/user-experience`,
      uuid,
      ratingOptedOut,
      showPrivacyPolicy: false,
      currentRating: null as UserExperienceRating | null,
      currentComments: null as string | null,
      question: Math.random() > 0.5 ?
        "Does this spark your curiosity?" :
        "Are you learning something new?",
    };
  },

  mounted() {

    if (!this.isWebGL2Enabled()) {
      this.showWebGL2Warning = true;
      this.layersLoaded = true;
      this.positionSet = true;
      this.showSplashScreen = false;
      // eslint-disable-next-lint @typescript-eslint/ban-ts-comment
      // @ts-expect-error `canvas` is defined
      WWTControl.singleton.canvas.setAttribute("hidden", "true");
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      WWTControl.singleton.renderOneFrame = function() {};
      return;
    }

    this.waitForReady().then(async () => {

      // Unlike the other things we're hacking here,
      // we aren't overwriting a method on a singleton instance (WWTControl)
      // or a static method (Constellations, Grids)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      SpreadSheetLayer.prototype.draw = drawSpreadSheetLayer;

      this.imagesetFolder = await this.loadImageCollection({
        url: this.wtml.c2022e3,
        loadChildFolders: false
      });
      const children = this.imagesetFolder.get_children() ?? [];
      const layerPromises: Promise<Layer>[] = [];
      children.forEach((item) => {
        if (!(item instanceof Place)) { return; }
        const imageset = item.get_backgroundImageset() ?? item.get_studyImageset();
        if (imageset == null) { return; }
        const name = imageset.get_name();
        layerPromises.push(this.addImageSetLayer({
          url: imageset.get_url(),
          mode: "autodetect",
          name: name,
          goto: false
        }).then((layer) => {
          this.imagesetLayers[name] = layer;
          applyImageSetLayerSetting(layer, ["opacity", 0]);
          return layer;
        }));
      });
      

      this.loadImageCollection({
        url: this.bgWtml,
        loadChildFolders: true,
      });
      
      this.backgroundImagesets = [...skyBackgroundImagesets];

      this.getLocation(true);
      this.setClockSync(false);
      // create date with y m d h m s

      layerPromises.push(this.createTableLayer({
        name: "All Dates",
        referenceFrame: "Sky",
        dataCsv: fullDatesString
      }).then((layer) => {
        layer.set_lngColumn(1);
        layer.set_latColumn(2);
        layer.set_markerScale(MarkerScales.screen);
        this.applyTableLayerSettings({
          id: layer.id.toString(),
          settings: [
            ["scaleFactor", 2.5],
            ["plotType", PlotTypes.point],
            ["color", Color.fromHex(this.ephemerisColor)],
            //["sizeColumn", 4],
            //["pointScaleType", PointScaleTypes.log],
            ["opacity", 0.8]
          ]
        });
        return layer;
      }));

      layerPromises.push(this.createTableLayer({
        name: "Comet Image Dates",
        referenceFrame: "Sky",
        dataCsv: cometImageDatesString
      }).then((layer) => {
        layer.set_lngColumn(1);
        layer.set_latColumn(2);
        layer.set_markerScale(MarkerScales.screen);
        this.applyTableLayerSettings({
          id: layer.id.toString(),
          settings: [
            ["scaleFactor", 4],
            ["color", Color.fromHex('#FFFFFF')],
            ["plotType", PlotTypes.point],
            //["sizeColumn", 3],
            ["opacity", 0.4]
          ]
        });
        return layer;
      }));

      this.setTime(this.dateTime);

      this.showControls = !this.mobile;

      Promise.all(layerPromises).then(() => {
        this.layersLoaded = true;
        
        // Set all of the imageset layers to be above the spreadsheet layers
        this.resetImagesetLayerOrder();

        const splashScreenListener = (_event: KeyboardEvent) => {
          this.showSplashScreen = false;
          window.removeEventListener('keyup', splashScreenListener);
        };
        window.addEventListener('keyup', splashScreenListener);

        window.addEventListener('keyup', (event: KeyboardEvent) => {
          if (["Esc", "Escape"].includes(event.key) && this.showVideoSheet) {
            this.showVideoSheet = false;
          }
        });
      });

      this.wwtSettings.set_localHorizonMode(true);
      this.wwtSettings.set_showAltAzGrid(this.showAltAzGrid);
      this.wwtSettings.set_showAltAzGridText(this.showAltAzGrid);
      this.wwtSettings.set_showConstellationLabels(this.showConstellations);
      this.wwtSettings.set_showConstellationFigures(this.showConstellations);

      // This is kinda horrible, but it works!

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.wwtControl._drawSkyOverlays = drawSkyOverlays;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      Constellations.initializeConstellationNames = initializeConstellationNames;
      Grids._makeAltAzGridText = makeAltAzGridText;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      LayerManager._draw = layerManagerDraw;

      this.updateWWTLocation();

      // wwtZoomDeg is still 0 if we run this here
      // and it was the same in nextTick
      // so give just a bit of a delay
      setTimeout(() => {
        this.centerOnCurrentDate();
        this.positionSet = true;
      }, 100);

      // this.ratingDisplaySetup();

    });

  },

  computed: {

    dateTime() {
      const todMs = this.dayFrac * MILLISECONDS_PER_DAY;
      return new Date(this.selectedDate.getTime() + todMs);
    },

    selectedTimezoneOffset() {
      return getTimezoneOffset(this.selectedTimezone);
    },

    isLoading(): boolean {
      return !this.ready;
    },
    ready(): boolean {
      return this.layersLoaded && this.positionSet;
    },
    selectedDate(): Date {
      return new Date(this.selectedTime);
    },
    smallSize(): boolean {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return this.$vuetify.display.smAndDown;
    },

    xSmallSize(): boolean {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return this.$vuetify.display.xs;
    },

    mobile(): boolean {
      return this.smallSize && this.touchscreen;
    },
    cssVars() {
      return{
        '--comet-color': this.cometColor,
        '--ephemeris-color': this.ephemerisColor,
        '--app-content-height': this.showTextSheet ? '66%' : '100%',
      };
    },
    wwtControl(): WWTControl {
      return WWTControl.singleton;
    },
    wwtRenderContext(): RenderContext {
      return this.wwtControl.renderContext;
    },
    wwtSettings(): Settings {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return Settings.get_active();
    },
    dayFrac(): number {
      const dateForTOD = new Date();
      const timezoneOffsetHours = this.selectedTimezoneOffset / (60*60*1000);
      dateForTOD.setUTCHours(this.timeOfDay.hours - timezoneOffsetHours, this.timeOfDay.minutes, this.timeOfDay.seconds);
      const todMs = 1000 * (3600 * dateForTOD.getUTCHours() + 60 * dateForTOD.getUTCMinutes() + dateForTOD.getUTCSeconds());
      return todMs / MILLISECONDS_PER_DAY;
    },

    dontSetTime(): boolean {
      return this.selectedTime %MILLISECONDS_PER_DAY !== 0;
    },
    
    showTextSheet: {
      get(): boolean {
        return this.sheet === 'text';
      },
      set(_value: boolean) {
        this.selectSheet('text');
        this.showTextTooltip = false;
      }
    },
    showVideoSheet: {
      get(): boolean {
        return this.sheet === "video";
      },
      set(value: boolean) {
        this.selectSheet('video');
        if (!value) {
          const video = document.querySelector("#info-video") as HTMLVideoElement;
          video.pause();
        }
        this.showVideoTooltip = false;
      }
    },
    // altAz() {
    //   return this.equatorialToHorizontal(this.wwtRARad, this.wwtDecRad, this.location.latitudeRad, this.location.longitudeRad, this.selectedDate || new Date());
    // }
  },

  methods: {

    clearPlayingInterval() {
      if (this.playingIntervalId !== null) {
        clearInterval(this.playingIntervalId);
        this.playingIntervalId = null;
      }
    },

    moveOneDayForward() {
      this.selectedTime += MILLISECONDS_PER_DAY;
    },

    moveOneDayBackward() {
      this.selectedTime -= MILLISECONDS_PER_DAY;
    },

    toDateString(date: Date) {
      // date = new Date(date.getTime() + this.selectedTimezoneOffset) // ignore timezone
      return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
    },

    interpolatedTable(table: Table): Table | null {
      const index = table.findIndex(r => r.date.getTime() === this.selectedTime);
      if (index === -1) { return null; }
      const row = table[index];
      const nextRow = table[index + 1] ?? row;
      const deltaT = (nextRow.date.getTime() - row.date.getTime()) / MILLISECONDS_PER_DAY;
      // get how far we are into the day
      const dayFracPassed = (row.date.getTime() / MILLISECONDS_PER_DAY) % 1;
      const f = (this.dayFrac - dayFracPassed) / deltaT;
      const interpolatedRA = (1 - f) * row.ra + f * nextRow.ra;
      const interpolatedDec = (1 - f) * row.dec + f * nextRow.dec;
      
      const interpolatedRow: TableRow = { ...row };
      interpolatedRow.ra = interpolatedRA;
      interpolatedRow.dec = interpolatedDec;
      return Object.assign([interpolatedRow], { columns: table.columns });
    },

    // JC: When the "allow imagesets above spreadsheets" functionality
    // gets added into the engine,
    // remember to add something like this along with it
    setSpreadSheetLayerOrder(id: string, order: number) {
      const layer = LayerManager.get_layerList()[id];
      const layers = LayerManager.get_allMaps()[layer.get_referenceFrame()].layers;
      if (order >= 0) {
        const currentIndex = layers.indexOf(layer);
        if (currentIndex > -1) {
          layers.splice(currentIndex, 1);
        }
        LayerManager.get_allMaps()[layer.get_referenceFrame()].layers.splice(order, 0, layer);
      }
    },

    updateLayersForDate() {

      this.interpolatedDailyTable = this.interpolatedTable(fullDatesTable);
      if (this.currentAllLayer !== null) {
        this.deleteLayer(this.currentAllLayer.id);
        this.currentAllLayer = null;
      }

      if (this.interpolatedDailyTable !== null) {
        this.createTableLayer({
          name: "Daily Date Layer",
          referenceFrame: "Sky",
          dataCsv: formatCsvTable(this.interpolatedDailyTable)
        }).then((layer) => {
          this.currentAllLayer = layer;
          layer.set_lngColumn(1);
          layer.set_latColumn(2);
          layer.set_markerScale(MarkerScales.screen);
          this.setSpreadSheetLayerOrder(layer.id.toString(), 0);
          this.applyTableLayerSettings({
            id: layer.id.toString(),
            settings: [
              ["scaleFactor", 5],
              ["plotType", PlotTypes.point],
              ["color", Color.fromHex('#E562BC')],
              //["sizeColumn", 3],
              ["opacity", 1],
            ]
          });
        });
      }
    },

    closeSplashScreen() {
      this.showSplashScreen = false;
    },
  
    updateWWTLocation() {
      this.wwtSettings.set_locationLat(R2D * this.location.latitudeRad);
      this.wwtSettings.set_locationLng(R2D * this.location.longitudeRad );
    },

    logLocation() {
      console.log(this.location.latitudeRad * R2D, this.location.longitudeRad * R2D);
    },
    
    logPosition() {
      console.log(this.wwtRARad * R2D, this.wwtDecRad * R2D);
    },

    printUTCDate(date: Date) {
      return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()} ${date.getUTCHours()}:${date.getUTCMinutes()}:${date.getUTCSeconds()}`;
    },

    selectSheet(name: SheetType) {
      if (this.sheet == name) {
        this.sheet = null;
        this.$nextTick(() => {
          this.blurActiveElement();
        });
      } else {
        this.sheet = name;
      }
    },

    getImageSetLayerIndex(layer: ImageSetLayer): number {
      // find layer in this.wwtActiveLayers
      // this.wwtActiveLayers is a dictionary of {0:id1, 1:id2, 2:id3, ...}
      // get the key item with the value of layer.id
      for (const [key, value] of Object.entries(this.wwtActiveLayers)) {
        if (value === layer.id.toString()) {
          return Number(key);
        }
      }
      return -1;
    },

    resetImagesetLayerOrder() {
      // Reset the order of the imageset layers
      Object.keys(this.imagesetLayers).sort((k1, k2) => {
        return new Date(k1).getTime() - new Date(k2).getTime();
      }).forEach((key) => {
        const layer = this.imagesetLayers[key];
        this.setImageSetLayerOrder({
          id: layer.id.toString(),
          order: this.wwtActiveLayers.length
        });
      });
    },

    
    onItemSelected(place: Place) {
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      const layer = this.imagesetLayers[iset.get_name()];
      this.resetImagesetLayerOrder();
      this.setImageSetLayerOrder({
        id: layer.id.toString(),
        order: this.wwtActiveLayers.length + 1
      });
      const [month, day, year] = iset.get_name().split("/").map(x => parseInt(x));
      this.selectedTime = Date.UTC(year, month - 1, day); 
      this.showImageForDateTime(this.dateTime);
      // this.updateViewForDate();

      this.playing = false;
      this.playingCometPath = false;

      // Give time for the selectedTime changes to propagate
      setTimeout(() => {
        this.$nextTick(() => {
          if (this.imageOutOfView(place) || this.needToZoomIn(place)) {
            this.gotoRADecZoom({
              raRad: D2R * iset.get_centerX(),
              decRad: D2R * iset.get_centerY(),
              zoomDeg: place.get_zoomLevel() * 2.5,
              instant: true
            });
          }
        });
      }, 10);
      
    },

    wwtSmallestFov(): number {
      // ignore the possibility of rotation
      const w = this.wwtRenderContext.width;
      const h = this.wwtRenderContext.height;
      const fovH = this.wwtRenderContext.get_fovAngle() * D2R;
      const fovW = fovH * w / h;
      return Math.min(fovW, fovH);
    },

    radecInFOV(ra: number, dec: number, center_ra: number, center_dec: number, fov: number, fov_view: number, fraction_of_place: number): boolean {
      let dist = distance(ra, dec, center_ra, center_dec);
      dist += (fraction_of_place - 1/2) * fov;
      return dist < fov_view / 2;
    },

    moveIfTableRowOfFOV(position: TableRow) {
      // get imageset of date
      const move = this.radecInFOV(
        D2R * position.ra * D2R,
        D2R * position.dec * D2R,
        this.wwtRARad,
        this.wwtDecRad,
        10 * D2R,
        this.wwtRenderContext.get_fovAngle() * D2R,
        1/2
      );

      return move;
      
    },
    
    checkIfPlaceIsInTheCurrentFOV(place: Place, fraction_of_place = 1/2): boolean {
      // checks if the center of place is in the current field of view
      // Assume the Zoom level corresponds to the size of the image
      // fraction_of_place is ~fraction of the place that must be in the current FOV
      // by default, allow 1/3 of the place to be visible and still be considered in view
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) {
        return false;
      }

      const isetRa = iset.get_centerX() * D2R;
      const isetDec = iset.get_centerY() * D2R;
      const isetFov = (place.get_zoomLevel() / 6) * D2R;
      
      const curRa = this.wwtRARad;
      const curDec = this.wwtDecRad;
      const curFov = this.wwtSmallestFov();

      return this.radecInFOV(isetRa, isetDec, curRa, curDec, isetFov, curFov, fraction_of_place);
    },

    // convenience wrapper for (not checkIfPlaceIsInTheCurrentFOV)
    imageOutOfView(place: Place): boolean { return !this.checkIfPlaceIsInTheCurrentFOV(place); },

    needToZoomIn(place: Place, factor = 5): boolean {
      // 1) we are already zoomed all the way out (if FOV > 50)
      if (this.wwtZoomDeg > 300) { return true; }

      // 2) the image is too small (so it's fov < 1/6 of the current fov)
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset != null) {
        if (place.get_zoomLevel() * factor < this.wwtZoomDeg) { return true; }
      }
      
      return false;
    },

    onTimeSliderChange(options?: MoveOptions) {
      this.$nextTick(() => {
        this.showImageForDateTime(this.dateTime);
        this.updateViewForDate(options);
      });
    },

    
    
    onOpacityChanged(place: Place, opacity: number, move: boolean) {
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      this.updateImageOpacity(place, opacity);
      

      this.$nextTick(() => {
        const zoom = this.needToZoomIn(place, 2.5) ? place.get_zoomLevel() * 2.5 : this.wwtZoomDeg;
        if ((this.imageOutOfView(place) && move) || (this.needToZoomIn(place, 8) && move) ) {
          const [month, day, year] = iset.get_name().split("/").map(x => parseInt(x));
          this.selectedTime = Date.UTC(year, month - 1, day); 
          this.incomingItemSelect = place;

          this.gotoRADecZoom({
            raRad: D2R * iset.get_centerX(),
            decRad: D2R * iset.get_centerY(),
            zoomDeg: zoom,
            instant: false
          });
        }
      });
    },

    updateImageOpacity(place: Place, opacity: number) {
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; }
      const layer = this.imagesetLayers[iset.get_name()];
      if (layer == null) { return; }
      // applyImageSetLayerSetting(layer, ["opacity", opacity / 100]);
      // tell setLayerOpacityForImageSet that we are updating from the ui
      this.setLayerOpacityForImageSet(iset.get_name(), opacity / 100, true);
    },
    
    onToggle(place: Place, checked: boolean) {
      // when we toggle the image we want to set it's opacity to zero
      const iset = place.get_studyImageset() ?? place.get_backgroundImageset();
      if (iset == null) { return; } 
      const iname = iset.get_name();
      this.setLayerOpacityForImageSet(iname, checked ? 1 : 0);
    },
    
    setupLocationSelector() {
      const locationDeg: [number, number] = [R2D * this.location.latitudeRad, R2D * this.location.longitudeRad];
      const map = L.map("map-container").setView(locationDeg, 4);
      // L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      //   maxZoom: 19,
      //   attribution: '&copy; <a href="http://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
      //   className: 'map-tiles'
      // }).addTo(map);
      map.attributionControl.setPrefix('<a href="https://leafletjs.com/" target="_blank" rel="noopener noreferrer">Leaflet</a>');
      L.tileLayer('https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',{
        maxZoom: 20,
        subdomains:['mt0','mt1','mt2','mt3'],
        attribution: `&copy <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">Google Maps</a>`,
        className: 'map-tiles'
      }).addTo(map);

      this.circle = this.circleForLocation(...locationDeg).addTo(map);

      map.doubleClickZoom.disable();
      map.on('dblclick', this.onMapSelect);
      this.map = map;
    },

    onMapSelect(e: LeafletMouseEvent) {
      let lngRad = e.latlng.lng * D2R + Math.PI;
      const twoPi = 2 * Math.PI;
      lngRad = ((lngRad % twoPi) + twoPi) % twoPi; // We want modulo, but JS % operator is remainder
      lngRad -= Math.PI;
      this.location = {
        latitudeRad: e.latlng.lat * D2R,
        longitudeRad: lngRad
      };
    },

    circleForLocation(latDeg: number, lonDeg: number): L.Circle {
      return L.circle([latDeg, lonDeg], {
        color: "#FF0000",
        fillColor: "#FF0033",
        fillOpacity: 0.5,
        radius: 200
      });
    },

    getLocation(startup = false) {
      const options = { timeout: 10000, enableHighAccuracy: true };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.location = {
            longitudeRad: D2R * position.coords.longitude,
            latitudeRad: D2R * position.coords.latitude
          };

          if (this.map) {
            this.map.setView([position.coords.latitude, position.coords.longitude], this.map.getZoom());
          }
        },
        (_error) => {
          const msg = "Unable to autodetect location. Location will default to Cambridge, MA, USA, or you can\nuse the location selector to manually input a location.";
          if (startup) {
            this.$notify({
              group: "startup-location",
              type: "error",
              text: msg,
              duration: 4500
            });
          } else {
            this.locationErrorMessage = msg;
          }
          this.updateHorizon();
        },
        options
      );
    },

    // WWT does have all of this functionality built in
    // but it doesn't seem to be exposed
    // We should do that, but for now we just copy the web engine code
    // https://github.com/Carifio24/wwt-webgl-engine/blob/master/engine/wwtlib/Coordinates.cs
    altAzToHADec(altRad: number, azRad: number, latRad: number): { ra: number; dec: number; } {
      azRad = Math.PI - azRad;
      if (azRad < 0) {
        azRad += 2 * Math.PI;
      }
      let ra = Math.atan2(Math.sin(azRad), Math.cos(azRad) * Math.sin(latRad) + Math.tan(altRad) * Math.cos(latRad));
      if (ra < 0) {
        ra += 2 * Math.PI;
      }
      const dec = Math.asin(Math.sin(latRad) * Math.sin(altRad) - Math.cos(latRad) * Math.cos(altRad) * Math.cos(azRad));
      return { ra, dec };
    },

    getJulian(utc: Date): number {
      let year = utc.getUTCFullYear();
      let month = utc.getUTCMonth()+1;
      const day = utc.getUTCDate();
      const hour = utc.getUTCHours();
      const minute = utc.getUTCMinutes();
      const second = utc.getUTCSeconds() + utc.getUTCMilliseconds() / 1000.0;

      if (month == 1 || month == 2)
      {
        year -= 1;
        month += 12;
      }

      const a = Math.floor(year / 100);
      const b = 2 - a + Math.floor(a / 4.0);
      const c = Math.floor(365.25 * year);
      const d = Math.floor(30.6001 * (month + 1));

      // gives julian date: number of days since Jan 1, 4713 BC
      const jd = b + c + d + 1720994.5 + day + (hour + minute / 60.00 + second / 3600.00) / 24.00;
      return jd;

    },
    
    mstFromUTC2(utc: Date, longRad: number): number {
      const lng = longRad * R2D;

      const modifiedJD = this.getJulian(utc)  - 2451545;

      const julianCenturies = modifiedJD / 36525.0;
      // this form wants julianDays - 2451545
      let mst = 280.46061837 + 360.98564736629 * modifiedJD + 0.000387933 * julianCenturies * julianCenturies - julianCenturies * julianCenturies * julianCenturies / 38710000 + lng;

      if (mst > 0.0) {
        while (mst > 360.0) {
          mst = mst - 360.0;
        }
      } else {
        while (mst < 0.0) {
          mst = mst + 360.0;
        }
      }

      return mst;
    },

    horizontalToEquatorial(altRad: number, azRad: number, latRad: number, longRad: number, utc: Date): EquatorialRad {
      const st = this.mstFromUTC2(utc, longRad); // siderial time 
  
      const haDec = this.altAzToHADec(altRad, azRad, latRad); // get Hour Angle and Declination
      
      const ha = haDec.ra * R2D;

      let ra = st + ha;
      if (ra < 0) {
        ra += 360;
      }
      if (ra > 360) {
        ra -= 360;
      }
      // ra -= 180;
      // console.log(`Alt: ${(altRad*R2D).toFixed(2)} Az: ${(azRad*R2D).toFixed(2)} Ra: ${ra.toFixed(2)} Dec: ${(haDec.dec*R2D).toFixed(2)}`)

      return { raRad: D2R * ra, decRad: haDec.dec };
    },

    equatorialToHorizontal(raRad: number, decRad: number, latRad: number, longRad: number, utc: Date): HorizontalRad {
      let hourAngle = this.mstFromUTC2(utc, longRad) - R2D * raRad;
      if (hourAngle < 0) {
        hourAngle += 360;
      }

      const ha = D2R * hourAngle;
      const dec = decRad;
      const lat = latRad;
      
      const sinAlt = Math.sin(dec) * Math.sin(lat) + Math.cos(dec) * Math.cos(lat) * Math.cos(ha);
      const altitude = Math.asin(sinAlt);
      const cosAz = (Math.sin(dec) - Math.sin(altitude) * Math.sin(lat)) / (Math.cos(altitude) * Math.cos(lat));
      let azimuth = Math.acos(cosAz);

      azimuth = azimuth + (Math.PI * 80) % (Math.PI * 2);

      if (Math.sin(ha) > 0) {
        azimuth = 2 * Math.PI - azimuth;
      }
      return { altRad: altitude, azRad: azimuth };

    },

    createHorizon(when: Date | null = null) {
      this.removeHorizon();

      const color = '#01362C';
      const date = when || this.dateTime || new Date();

      // The initial coordinates are given in Alt/Az, then converted to RA/Dec
      // Use N annotations to cover below the horizon
      const n = 6;
      const delta = 2 * Math.PI / n;
      for (let i = 0; i < n; i++) {
        let points: [number, number][] = [
          [0, i * delta],
          [-Math.PI / 2, i * delta],
          [0, (i + 1) * delta]
        ];
        points = points.map((point) => {
          const raDec = this.horizontalToEquatorial(...point, this.location.latitudeRad, this.location.longitudeRad, date);
          return [R2D * raDec.raRad, R2D * raDec.decRad];
        });
        const poly = new Poly();
        points.forEach(point => poly.addPoint(...point));
        poly.set_lineColor(color);
        poly.set_fill(true);
        poly.set_fillColor(color);
        this.addAnnotation(poly);
      }

    },

    closestPoint(
      point: { x: number; y: number; },
      threshold?: number
    ): TableRow | null {
      const raDecDeg = this.findRADecForScreenPoint(point);
      const target = { ra: D2R * raDecDeg.ra, dec: D2R * raDecDeg.dec };

      let minDist = Infinity;
      let closestPt = null as TableRow | null;

      [cometImageDatesTable, fullDatesTable].forEach((table) => {
        table.forEach(row => {
          const raRad = row.ra * D2R;
          const decRad = row.dec * D2R;

          const dist = distance(target.ra, target.dec, raRad, decRad);
          if (dist < minDist) {
            closestPt = row;
            minDist = dist;
          }

        });
      });

      if (closestPt !== null) {
        const closestScreenPoint = this.findScreenPointForRADec(closestPt);
        const pixelDist = Math.sqrt((point.x - closestScreenPoint.x) ** 2 + (point.y - closestScreenPoint.y) ** 2);
        if (!threshold || pixelDist < threshold) {
          return closestPt;
        }
      }
      return null;
    },

    removeHorizon() {
      this.clearAnnotations();
    },

    updateLastClosePoint(event: PointerEvent) {
      const pt = { x: event.offsetX, y: event.offsetY };
      const closestPt = this.closestPoint(pt, this.selectionProximity);
      if (closestPt == null && this.lastClosePt == null) {
        return;
      }
      const needsUpdate =
        closestPt == null ||
        this.lastClosePt == null ||
        this.lastClosePt.ra != closestPt.ra ||
        this.lastClosePt.dec != closestPt.dec;
      if (needsUpdate) {
        this.lastClosePt = closestPt;
      }
    },

    onPointerMove(event: PointerEvent) {
      if (!this.isPointerMoving && this.pointerStartPosition !== null) {
        const dist = Math.sqrt((event.pageX - this.pointerStartPosition.x) ** 2 + (event.pageY - this.pointerStartPosition.y) ** 2);
        if (dist > this.pointerMoveThreshold) {
          this.isPointerMoving = true;
        }
      }

      this.updateLastClosePoint(event);
    },

    onPointerDown(event: PointerEvent) {
      this.isPointerMoving = false;
      this.pointerStartPosition = { x: event.pageX, y: event.pageY };
    },

    onPointerUp(event: PointerEvent) {
      if (!this.isPointerMoving) {
        this.updateLastClosePoint(event);
        if (this.lastClosePt !== null) {
          this.selectedTime = this.lastClosePt.date.getTime();
          this.$nextTick(() => {
            this.onTimeSliderChange();
            this.updateViewForDate();
          });
        }
      }
      this.pointerStartPosition = null;
      this.isPointerMoving = false;
    },

    updateViewForDate(options?: MoveOptions) {
      let position = null as TableRow | null;

      const cometImageIndex = cometImageDates.findIndex(d => d === this.selectedTime);
      
      if (cometImageIndex > -1) {
        if (this.interpolatedDailyTable !== null) {
          position = this.interpolatedDailyTable[0];
        } else {
          position = cometImageDatesTable[cometImageIndex];
        }
      } else {
        const allIndex = allDates.findIndex(d => d === this.selectedTime);
        if (allIndex > -1) {
          position = fullDatesTable[allIndex];
        }
      }

      if (position !== null) {
        this.gotoRADecZoom({
          raRad: D2R * position.ra,
          decRad: D2R * position.dec,
          zoomDeg: options?.zoomDeg ?? this.wwtZoomDeg,
          rollRad: options?.rollRad ?? this.wwtRollRad,
          instant: options?.instant ?? true
        });
      }

    },
    
    namefromDate(date: Date): string {
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth() + 1;
      const day = date.getUTCDate();
      return `${month}/${day}/${year}`;
    },

    matchImageSetName(date: Date): string {
      // imageset names are keys in this.imagesetLayers
      const imagesetNames = Object.keys(this.imagesetLayers);
      // loop over image set names. find the name (which is a MM/DD/YYYY date string) 
      // that is or comes after the date we are looking for
      
      for (const name of imagesetNames) {
        // convert the name to a date
        // if the name is after the date we are looking for, return it
        const wwtDateString = this.namefromDate(date);
        if (name == wwtDateString) {
          return name;
        }
      }
      return '';
    },

    setLayerOpacityForImageSet(name: string, opacity: number, setting_opacity_from_ui=false) {
      const layer = this.imagesetLayers[name];
      if (layer != null) {
        // update the image opacity in the WWT control
        applyImageSetLayerSetting(layer, ['opacity', opacity]);

        // update the value for the slider only if we are not setting the opacity from the UI
        if (!setting_opacity_from_ui) {
          const selector = `#items div.item[title='${name}'] input.opacity-range[type='range']`;
          const el = (document.querySelector(selector) as HTMLInputElement);
          if (el != null) {
            el.value = `${opacity * 100}`;
          }
        }

        const toggleSelector = `#items input[type='checkbox'][title='${name}']`;
        const el2 = (document.querySelector(toggleSelector) as HTMLInputElement);
        // truth table: opacity == 0 and el.checked == false => do nothing
        // truth table: opacity == 0 and el.checked == true => set el.checked = false
        // truth table: opacity > 0 and el.checked == false => set el.checked = true
        // truth table: opacity > 0 and el.checked == true => do nothing
        if (el2 != null) {
          if (opacity == 0 && el2.checked) {
            el2.checked = false;
          } else if (opacity > 0 && !el2.checked) {
            el2.checked = true;
          }
        }
        
      }
    },
    
    showImageForDateTime(date: Date): boolean {
      const name = this.matchImageSetName(date);
      if (name == null) {
        this.incomingItemSelect = null;
        return false;
      }
      const imagesetNames = Object.keys(this.imagesetLayers);
      let shown = false;
      imagesetNames.forEach((iname: string) => {
        if (iname != name) {
          this.setLayerOpacityForImageSet(iname, 0);
        } else {
          this.setLayerOpacityForImageSet(iname, 1);
          shown = true;
          // need to get the Place object for the image set and use it to set the view
          if (this.imagesetFolder != null) {
            const places = this.imagesetFolder.get_children();
            if (places != null) {
              const place = places.filter((place) => place.get_name() == iname);
              this.incomingItemSelect = place[0];
            }
          }
          // const iset = this.wwtControl.getImagesetByName(iname)
          // if (iset == null) { return; }
          // this.gotoRADecZoom({
          //   raRad: D2R * iset.get_centerX(),
          //   decRad: D2R * iset.get_centerY(),
          //   zoomDeg: this.wwtZoomDeg,
          //   instant: true
          // });
        }
      });
      return shown;
    },

    centerOnCurrentDate(options?: MoveOptions) {
      const now = new Date();
      const localOffset = now.getTimezoneOffset() * 60 * 1000;
      const hours = now.getHours() + (this.selectedTimezoneOffset + localOffset) / (1000 * 60 * 60);
      this.timeOfDay = { hours: hours, minutes: now.getMinutes(), seconds: now.getSeconds() };
      this.selectedTime = now.setUTCHours(0, 0, 0, 0);
      this.$nextTick(() => {
        this.updateViewForDate(options);
      });
    },

    updateForDateTime() {
      if (!this.dontSetTime) { this.setTime(this.dateTime); }
      this.updateHorizon(this.dateTime); 
      // this.showImageForDateTime(this.dateTime);
      // this.updateViewForDate(options);
      this.updateLayersForDate();
    },

    updateHorizon(when: Date | null = null) {
      if (this.showHorizon) {
        this.createHorizon(when);
      } else {
        this.removeHorizon();
      }
    },

    setToFirstCometImage() {
      this.selectedTime = Date.UTC(2022, 11, 18);
      const children = this.imagesetFolder?.get_children();
      if (children == null) { return; }
      const place = children[0] as Place;
      this.onItemSelected(place);
    },

    async ratingDisplaySetup() {
      if (this.ratingOptedOut) {
        return;
      }
      
      const existsResponse = await fetch(`${this.storyRatingUrl}/${this.uuid}`, {
        method: "GET",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
      });

      // NB: If we want to ask multiple questions, this logic can be adjusted
      const existsContent = await existsResponse.json();
      const exists = existsResponse.status === 200 && existsContent.ratings?.length > 0;

      if (exists) {
        return;
      }

      setTimeout(() => {
        this.showRating = true;
      }, 30_000);
    },

    updateUserExperienceInfo(rating: UserExperienceRating | null, comments: string | null) {
      const body: Record<string, unknown> = {
        uuid: this.uuid,
        question: this.question,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        story_name: "green-comet",
      };
      if (rating) {
        body.rating = rating;
      }
      if (comments) {
        body.comments = comments;
      }
      fetch(this.storyRatingUrl, {
        method: "PUT",
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
    },
    
    onOptOutClicked() {
      this.showRating = false;
      this.ratingOptedOut = true;
      window.localStorage.setItem("cds-green-comet-rating-optout", "true");
    },

    isWebGL2Enabled(): boolean {
      // It doesn't seem like there's a better way to do this than just to try and get a context
      // https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Detect_WebGL
      // NB: The engine specifically wants a webgl2 context
      const canvas = document.createElement("canvas");
      const gl = canvas.getContext("webgl2");
      return gl instanceof WebGL2RenderingContext;
    },
  },

  watch: {
    showAltAzGrid(show: boolean) {
      this.wwtSettings.set_showAltAzGrid(show);
      this.wwtSettings.set_showAltAzGridText(show);
    },
    showConstellations(show: boolean) {
      this.wwtSettings.set_showConstellationLabels(show);
      this.wwtSettings.set_showConstellationFigures(show);
    },
    showHorizon(_show: boolean) {
      this.updateHorizon();
    },
    timeOfDay(_time: { hours: number; minutes: number; seconds: number }) {
      this.updateForDateTime();
    },
    location(loc: LocationRad, oldLoc: LocationRad) {
      const locationDeg: [number, number] = [R2D * loc.latitudeRad, R2D * loc.longitudeRad];
      if (this.map) {
        this.circle?.remove();
        this.circle = this.circleForLocation(...locationDeg).addTo(this.map as Map); // Not sure, why, but TS is cranky w/o casting
      }

      if (oldLoc.latitudeRad * loc.latitudeRad < 0) {
        Grids._altAzTextBatch = null;
      }

      this.selectedTimezone = tzlookup(...locationDeg);
      this.updateWWTLocation();

      // We need to let the location update before we redraw the horizon
      this.$nextTick(() => {
        this.updateHorizon();
      });
    },

    selectedDate() {
      this.updateForDateTime();
    },
    
    showLocationSelector(show: boolean) {
      if (show) {
        this.locationErrorMessage = "";
        this.$nextTick(() => {
          this.setupLocationSelector();
        });
      } else {
        this.map?.remove();
        this.circle = null;
      }
    },
    selectedTimezone(newTz: string, oldTz: string) {
      const newOffset = getTimezoneOffset(newTz);
      const oldOffset = getTimezoneOffset(oldTz);
      let newHours = this.timeOfDay.hours + ((newOffset - oldOffset) / (1000*60*60));
      if (newHours >= 24) {
        newHours -= 24;
        this.moveOneDayForward();
      } else if (newHours < 0) {
        newHours += 24;
        this.moveOneDayBackward();
      }
      this.timeOfDay.hours = newHours;
    },
    
    playing(play: boolean) {
      this.clearPlayingInterval();
      if (play) {
        this.playingIntervalId = setInterval(() => {
          if (this.selectedTime < maxDate) {
            this.moveOneDayForward();
          } else {
            this.selectedTime = minDate;
          }
          this.$nextTick(() => {
            this.showImageForDateTime(this.dateTime);
            this.updateViewForDate();
          });
        }, 350);
      }
    },

    playingCometPath(play: boolean) {
      this.clearPlayingInterval();
      if (!play) {
        return;
      }
      const minTime = Math.min(...cometImageDates) - MILLISECONDS_PER_DAY;
      const maxTime = Math.max(...cometImageDates) + MILLISECONDS_PER_DAY;

      if(this.selectedTime < minTime || this.selectedTime >= maxTime){
        this.selectedTime = minTime;
      }

      this.updateViewForDate({ zoomDeg: 60 });

      this.playingIntervalId = setInterval(() => {
        if (this.playingWaitCount > 0) {
          this.playingWaitCount -= 1;
          return;
        }
        if (this.selectedTime < maxTime) {
          this.moveOneDayForward();
          this.$nextTick(() => {
            const image = this.showImageForDateTime(this.dateTime);
            this.updateViewForDate();
            if (image) {
              this.playingWaitCount = 1;
            }
          });
        } else {
          this.playingCometPath = false;
        }
      }, 500);
    }
  }
});

</script>

<style lang="less">
@font-face {
  font-family: "Highway Gothic Narrow";
  src: url("https://projects.cosmicds.cfa.harvard.edu/cds-website/fonts/HighwayGothicNarrow.ttf");
}

:root {
  --default-font-size: clamp(0.7rem, min(1.7vh, 1.7vw), 1.1rem);
  --default-line-height: clamp(1rem, min(2.2vh, 2.2vw), 1.6rem);
  --time-content-max-width: 700px;
}

html {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #000;
  overflow: hidden;

  ::-webkit-scrollbar {
    // display: none;
  }
  
  -ms-overflow-style: none;
  // scrollbar-width: none;
}

body {
  position: fixed;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;

  font-family: Verdana, Arial, Helvetica, sans-serif;
}

#main-content {
  position: fixed;
  width: 100%;
  height: var(--app-content-height);
  overflow: hidden;

  transition: height 0.1s ease-in-out;
}

#app {
  width: 100%;
  height: 100%;
  margin: 0;
  overflow: hidden;

  .wwtelescope-component {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    border-style: none;
    border-width: 0;
    margin: 0;
    padding: 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.modal {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  z-index: 100;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

#modal-loading {
  background-color: #000;
  .container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    .spinner {
      background-image: url("https://projects.cosmicds.cfa.harvard.edu/cds-website/misc/lunar_loader.gif");
      background-repeat: no-repeat;
      background-size: contain;
      width: 3rem;
      height: 3rem;
    }
    p {
      margin: 0 0 0 1rem;
      padding: 0;
      font-size: 150%;
    }
  }
}

.pointer {
  cursor: pointer;
}

.control-icon {
  pointer-events: auto;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    color: white;
  }
}

.icon-wrapper {
  padding: 8px 16px;
}

#play-pause-icon-wrapper {
  color: var(--ephemeris-color);
  border-color: var(--ephemeris-color);
  margin-left: 2rem;

  &:focus {
    color: white;
  }
}

#video-icon-dummy {
  pointer-events: none;
  visibility: hidden;
}

.top-content {
  position: absolute;
  top: 1rem;
  left: 1rem;
  width: calc(100% - 2rem);
  pointer-events: none;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.bottom-content {
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: calc(100% - 2rem);
  pointer-events: none;
  align-items: center;
  gap: 5px;
}

#tools {
  z-index: 10;
  color: #fff;
  width: 100%;

  .opacity-range {
    width: 50vw;
  }

  .clickable {
    cursor: pointer;
  }

  select {
    background: white;
    color: black;
    border-radius: 3px;
  }
}

.tool-container {
  display: flex;
  width: 99%;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  pointer-events: auto;
}

.folder-view {
  max-height: calc(100% - 150px);
  width: ~"min(160px, 35vw)";
}

#controls {
  background: black;
  padding: 10px;
  border-radius: 10px;
  border: solid 1px var(--comet-color);
  display: flex;
  flex-direction: column;
  align-self: flex-end;
  margin-right: 1rem;
  margin-bottom: 1rem;
  pointer-events: auto;
  width: fit-content;

  .v-label {
    color: var(--comet-color);
    opacity: 1;
  }

  .controls-content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .v-btn {
      align-self: center;
      padding-left: 5px;
      padding-right: 5px;
      border: solid 1px #899499;

      &:focus {
        border: 2px solid white;
      }
    }

    .v-btn__content {
      color: black;
      font-weight: 900;
      font-size: 0.75em;
      white-space: break-spaces;
      width: 150px;
    }
  }

  #controls-top-row {
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: flex-end;
  }
}

.dp__select {
  color: #04D6B0 !important;
}

#show-controls {
  color: var(--comet-color);
}

a {
    text-decoration: none;
    font-weight: bold;
    color: #aec2fd; // lighter variant of sky color
    pointer-events: auto;

    &:hover {
      text-decoration: underline;
    }
  }

#credits {
  color: #ddd;
  font-size: calc(0.7em + 0.2vw);
  justify-self: flex-end;
  align-self: flex-end;
  display: flex;
  justify-content: center;
  align-items: center;

  p {
    margin: 0;
    padding: 0;
    line-height: 1;
  }

  a {
    text-decoration: none;
    color: #fff;
    pointer-events: auto;

    &:hover {
      text-decoration: underline;
    }

    &[class^="share-network"]:hover {
      text-decoration: none;
      filter: brightness(75%);
    }
  }

  img {
    height: 35px;
    vertical-align: middle;
    margin: 2px;
  }

  @media only screen and (max-width: 600px) {
  img {
    height: 24px;
  }
}

  svg {
    vertical-align: middle;
    height: 24px;
  }
}

.v-selection-control--focus-visible .v-selection-control__input::before {
  opacity: 0.25;
}

.ui-text {
  color: var(--comet-color);
  background: black;
  padding: 5px 5px;
  border: 2px solid black;
  border-radius: 10px;
  font-size: calc(0.8em + 0.25vw);

  &:focus {
    color: white;
  }
}

.ui-button {
  text-align: center;
  color: var(--comet-color);
  background: black;
  padding: 5px 5px;
  border: 2px solid var(--comet-color);
  border-radius: 10px;
  font-size: calc(0.7em + 0.2vw);
  user-select: none;
}

.clickable {
  pointer-events: auto;

  &:hover {
    cursor: pointer;
  }
}

#buttons-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-self: flex-end;
}

#center-view-button {
  margin-bottom: 25px;
}

#right-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.video-wrapper {
  height: 100%;
  background: black;
  text-align: center;
  z-index: 1000;
}

video {
  height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

#video-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 100%;
  overflow: hidden;
  padding: 0px;
  z-index: 1000;
}

.bottom-sheet {
  .v-overlay__content {
    align-self: flex-end;
    padding: 0;
    margin: 0;
    max-width: 100%;
    height: 34%;
  }
}

#tabs {
  width: calc(100% - 3em);
  align-self: left;
}

.info-text {
  height: 33vh;
  padding-bottom: 25px;

  & a {
    text-decoration: none;
  }
}

.close-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 15;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    color: white;
    border: 2px solid white;
  }
}

.scrollable {
  overflow-y: auto;
}

#tab-items {
  // padding-bottom: 2px !important;

  .v-card-text {
    font-size: ~"max(14px, calc(0.7em + 0.3vw))";
    padding-top: ~"max(2vw, 16px)";
    padding-left: ~"max(4vw, 16px)";
    padding-right: ~"max(4vw, 16px)";

    .end-spacer {
      height: 25px;
    }
  }

}

#close-text-icon {
  position: absolute;
  top: 0.25em;
  right: calc((3em - 0.6875em) / 3); // font-awesome-icons have width 0.6875em
  color: white;
}

#location-dialog {
  .v-overlay__content {
    width: fit-content !important;
  }
}

#location-selector {
  align-items: center;
  margin: auto;
  width: 70vw;
  height: fit-content;
}

#map-container {
  width: 60vw;
  height: 70vh;
  margin: auto;
  padding: 5px 0px;
  border-radius: 5px;
}

// This prevents the tabs from having some extra space to the left when the screen is small
// (around 400px or less)
.v-tabs:not(.v-tabs--vertical).v-tabs--right>.v-slide-group--is-overflowing.v-tabs-bar--is-mobile:not(.v-slide-group--has-affixes) .v-slide-group__next, .v-tabs:not(.v-tabs--vertical):not(.v-tabs--right)>.v-slide-group--is-overflowing.v-tabs-bar--is-mobile:not(.v-slide-group--has-affixes) .v-slide-group__prev {
  display: none;
}

// Styling the slider

#sliderlabel {
  padding: 5px 10px;
  margin:0 5px;
  color:#fff !important;
  background-color: rgba(214, 4, 147,0.7);
  overflow: visible;
}

#slider {
  width: 100% !important;
  margin: 5px 30px;
}

.vue-slider-process {
  background-color: white !important;
}

.vue-slider-dot-tooltip-inner {
  cursor: grab;
  padding: 4px 10px !important;
  color: white !important;
  background-color: #9A2976 !important;
  border: 1px solid #9A2976 !important;

  &:active {
    cursor: grabbing;
  }
}

.vue-slider-dot-handle {
  cursor: grab;
  background-color: #9A2976 !important;
  border: 1px solid black !important;

  &:active {
    cursor: grabbing;
  }
}

.mark-line {
  position: absolute;
  height: 20px;
  width: 2px;
  margin: 0;
  background-color: var(--comet-color);
  transform: translateX(-50%) translateY(calc(-50% + 2px));

}

.left-content {
  position: absolute;
  left: 1rem;
  top: 1rem;
  pointer-events: none;
  height: calc(100% - 2rem);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

@media(max-width: 600px) {
  .mark-line {
    display: none;
  }

  #location-selector {
    width: 80vw;
    height: 85vh;
  }

  #video-container {
    display: inherit;
  }

  #map-container {
    width: 70vw;
  }
}


#splash-overlay {
  align-items: center;
  justify-content: center;
  font-size: min(8vw, 7vh);
}

#splash-screen {
  color: #E0E0E0;

  @media (max-width: 699px) {
    max-height: 80vh;
    max-width: 90vw;
  }

  @media (min-width: 700px) {
    max-height: 85vh;
    max-width: min(70vw, 800px);
  }


  background-color: black;
  backdrop-filter: blur(5px);
  justify-content: space-around;
  align-content: center;
  padding-top: 4rem;
  padding-bottom: 1rem;

  border-radius: 30px;
  border: min(1.2vw, 0.9vh) solid #6facf1;
  overflow: auto;
  font-family: 'Highway Gothic Narrow', 'Roboto', sans-serif;

  div {
    margin-inline: auto;
    text-align: center;
  }
  // make a paragraph inside the div centered horizontally and vertically
  p {
    font-family: 'Highway Gothic Narrow', 'Roboto', sans-serif;
    font-weight: bold;
    vertical-align: middle;
  }
    
  span.highlight {
    color: var(--comet-color);
    text-transform: uppercase;
    font-weight: bolder;
  }
  
  p.small {
    font-size: var(--default-font-size);
    font-weight: bold;
  }

  #first-splash-row {
    width: 100%;
  }

  #close-splash-button {
    position: absolute;
    top: 20px;
    right: 20px;
    text-align: end;
    color: var(--comet-color);
    font-size: min(5vw, 4vh);

    &:hover {
      cursor: pointer;
    }
  }

  #splash-screen-text {
    // in the grid, the text is in the 2nd column
    display: flex;
    flex-direction: column;
    line-height: 130%;
    
  }

  .splash-get-started {
    border: 2px solid white;
    font-size: calc(1.8 * var(--default-font-size));
    margin-top: 5%;
    margin-bottom: 2%;
    font-weight: bold !important;
  }

  #splash-screen-guide {
    margin-block: 1.5em;
    font-size: min(5vw, 4vh);
    line-height: 140%;
    width: 75%;

    .v-col{
      padding: 0;
    }
    
    .svg-inline--fa {
      color:var(--comet-color);
      margin: 0 10px;
    }
  }

  #splash-screen-acknowledgements {
    margin-top: 3rem;
    font-size: calc(1.7 * var(--default-font-size));
    line-height: calc(1.5 * var(--default-line-height));
    width: 60%; 
  }

  #splash-screen-logos {
    margin-block: 0.75em;

    img {
    height: 5vmin;
    vertical-align: middle;
    margin: 2px;
    }

    @media only screen and (max-width: 600px) {
      img {
        height: 24px;
      }
    }

    svg {
      vertical-align: middle;
      height: 24px;
    }
  }
}

#overlays {
  margin: 5px;
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
}

ul.text-list {
  margin-left:1em;
  margin-top: 0.5em;
}

div.credits {
  font-size: 0.8em;
}

// For styling the time picker
// See more info here:
// https://vue3datepicker.com/customization/theming/
.dp__theme_dark {
  --dp-background-color: black !important;
}

.dp__main {
  width: 175px;
}

* {
  --v-input-control-height: 40px;
}

// :root {
//   --map-tiles-filter: brightness(0.6) invert(1) contrast(3) hue-rotate(200deg) saturate(0.3) brightness(0.7);
// }

// @media (prefers-color-scheme: dark) {
//   .map-tiles {
//     filter:var(--map-tiles-filter, none);
//   }
// }

span.ui-element-ref-comet {
  background-color: #9A2976; /* #04D6B0; */
  padding: 0em 0.1em;
  border-radius: 0.2em;
}

/* from https://www.smashingmagazine.com/2021/12/create-custom-range-input-consistent-browsers/ */
input[type="range"] {
    -webkit-appearance: inherit;
    -moz-appearance: inherit;
    appearance: inherit;
    margin: 2px;
    --track-height: 1em;
    --thumb-radius: 0.8em;
    --thumb-color: rgba(205, 54, 157  , 0.7);
    // --thumb-color: #444;
    --track-color: rgba(4, 147, 214, .1);
    // --thumb-border: 1px solid #899499;
    --thumb-border-width: 1px;
    --thumb-border: var(--thumb-border-width) solid rgb(255, 255, 255);
    --track-border-width: 1px;
    --track-border: var(--track-border-width) solid rgba(4, 147, 214, 1);
    --thumb-margin-top: calc((var(--track-height) - 2*var(--track-border-width)) / 2 - (var(--thumb-radius)) / 2);
    
    &:hover {
      opacity: 1;
      --track-color: rgba(217, 234, 242,0.2);
      --thumb-color: rgba(205, 54, 157  , 1);
    }

    &:focus {
      border-radius: calc(var(--track-height) / 2);
    }
  }
  
  
  
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: inherit;
    -moz-appearance: inherit;
    appearance: inherit;
    width: var(--thumb-radius);
    height: var(--thumb-radius);
    margin-top: var(--thumb-margin-top);
    border-radius: 50%;
    background: var(--thumb-color);
    border: var(--thumb-border);
  }
  
  input[type="range"]::-moz-range-thumb {
    -webkit-appearance: inherit;
    -moz-appearance: inherit;
    appearance: inherit;
    width: var(--thumb-radius);
    height: var(--thumb-radius);
    margin-top: var(--thumb-margin-top);
    border-radius: 50%;
    background: var(--thumb-color);
    cursor: pointer;
    border: var(--thumb-border)
  }
  
  input[type="range"]::-webkit-slider-runnable-track {
    background: var(--track-color);
    /* outline: 1px solid white; */
    border-radius: calc(var(--track-height) / 2);
    border: var(--track-border);
    height: var(--track-height);
    margin-top: 0;
    padding: 0 calc((var(--track-height) - var(--thumb-radius))/2);
  }
  
  
input[type="range"]::-moz-range-track {
    background: var(--track-color);
    /* outline: 1px solid white; */
    border-radius: calc(var(--track-height) / 2);
    border: var(--track-border);
    height:var(--track-height);
    margin-top: 0;
    padding: 0 calc((var(--track-height) - var(--thumb-radius))/2);
  }

.rating-root {
  position: absolute !important;
  right: 5px;
  bottom: 0;
  padding: 5px;
  width: fit-content !important;
  // left: 50%;
  // transform: translateX(-50%);
  gap: 0 !important;
  border: solid 1px #EFEFEF !important;
  border-radius: 10px !important;
  background-color: #222222 !important;
  opacity: 0.95 !important;
  z-index: 20000;

  .rating-title {
    color: #EFEFEF;
    font-size: var(--default-font-size);
  }

  .rating-icon-row {
    
    padding: 0px;

    .svg-inline--fa {
      height: 30px;
    }
  }

  .comments-box {
    width: 100%;
    margin-top: 20px;
  }

  .v-card-text {
    padding-bottom: 0;
  }

  .v-card-actions {
    padding: 0;
  }
  
  #user-experience-footer {
    display: flex;
    flex-direction: row;
    gap: 5px;
  }

  .close-button {
    position: absolute !important;
    color: white !important;
  }

  .v-field--variant-filled .v-field__outline:before, .v-field--variant-underlined .v-field__outline:before, 
  .v-field--variant-filled .v-field__outline:after, .v-field--variant-underlined .v-field__outline:after {
    border-style: none !important;
  }
}

.error-dialog {
  width: auto;
  height: auto;
  max-width: 500px;
  border-radius: 10px;

  .v-card {
    border-radius: 10px !important;
  }
}

.error-message {
  padding: 1rem;
  border: 1px solid var(--comet-color);
  text-align: center;
  border-radius: 10px;
}
</style>
